import React, { useEffect, useState } from 'react';
import { api } from './api';
import { t, tn, getLang } from './i18n';

// ─────────────────────────────────────────────────────────────────
// Användarvillkor och integritetspolicy (api/src/routes/legal.ts).
//  - LegalPage: /villkor och /integritetspolicy, renderar public/legal/<doc>.md
//    (engelsk version <doc>.en.md om den finns). Saknas filen visas att texten
//    kommer inom kort.
//  - TermsCheckbox: krävs vid registrering när en villkorsversion är satt.
//  - TermsUpdateModal: inloggade som inte godkänt gällande version.
// ─────────────────────────────────────────────────────────────────

export type LegalDoc = 'villkor' | 'integritetspolicy';
export const LEGAL_PATHS: Record<LegalDoc, string> = { villkor: '/villkor', integritetspolicy: '/integritetspolicy' };

const PRIMARY = 'hsl(var(--primary))';

/** Gällande villkorsversion (null = inga krav än). Hämtas en gång per sidladdning. */
let configPromise: Promise<string | null> | null = null;
export function useTermsVersion(): string | null | undefined {
  const [v, setV] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    configPromise ??= api.legalConfig().then(c => c.termsVersion).catch(() => null);
    configPromise.then(setV);
  }, []);
  return v;
}

function inline(text: string, key: string): React.ReactNode[] {
  // **fet**, [länk](url)
  const out: React.ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) out.push(<strong key={`${key}-${i++}`}>{m[1]}</strong>);
    else {
      const href = m[3];
      const safe = /^(https?:|mailto:|\/)/.test(href) ? href : '#';
      out.push(<a key={`${key}-${i++}`} href={safe} style={{ color: '#111' }} {...(safe.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}>{m[2]}</a>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Minimal Markdown: rubriker (#–###), stycken, punkt- och numrerade listor, fet stil, länkar. */
export function renderMarkdown(md: string): React.ReactNode {
  const blocks = md.replace(/\r/g, '').split(/\n{2,}/);
  return blocks.map((block, bi) => {
    const lines = block.split('\n').filter(l => l.trim());
    if (!lines.length) return null;
    const h = /^(#{1,3})\s+(.*)$/.exec(lines[0]);
    if (h && lines.length === 1) {
      const size = { 1: 26, 2: 19, 3: 16 }[h[1].length as 1 | 2 | 3];
      return React.createElement(`h${h[1].length}`, { key: bi, style: { fontSize: size, margin: '28px 0 10px', color: '#111', fontWeight: 700 } }, inline(h[2], `h${bi}`));
    }
    if (lines.every(l => /^\s*[-*]\s+/.test(l))) {
      return <ul key={bi} style={{ paddingLeft: 22, margin: '8px 0' }}>{lines.map((l, i) => <li key={i} style={{ margin: '4px 0' }}>{inline(l.replace(/^\s*[-*]\s+/, ''), `${bi}-${i}`)}</li>)}</ul>;
    }
    if (lines.every(l => /^\s*\d+[.)]\s+/.test(l))) {
      return <ol key={bi} style={{ paddingLeft: 22, margin: '8px 0' }}>{lines.map((l, i) => <li key={i} style={{ margin: '4px 0' }}>{inline(l.replace(/^\s*\d+[.)]\s+/, ''), `${bi}-${i}`)}</li>)}</ol>;
    }
    return <p key={bi} style={{ margin: '10px 0' }}>{inline(lines.join(' '), `p${bi}`)}</p>;
  });
}

export function LegalPage({ doc }: { doc: LegalDoc }) {
  const [md, setMd] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    const load = (file: string) => fetch(`/legal/${file}`, { cache: 'no-cache' })
      // Firebase svarar med index.html (200) för filer som saknas — avvisa HTML.
      .then(r => (r.ok && !(r.headers.get('content-type') ?? '').includes('html') ? r.text() : null)).catch(() => null);
    (async () => {
      const text = (getLang() === 'en' ? await load(`${doc}.en.md`) : null) ?? await load(`${doc}.md`);
      setMd(text);
    })();
    document.title = `${doc === 'villkor' ? t('legal.terms') : t('legal.privacy')} — Qvestium`;
  }, [doc]);

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 16px 64px', fontSize: 15, lineHeight: 1.65, color: '#2b2b2b' }}>
        <a href="/" style={{ fontSize: 13, color: '#4a4a4a', textDecoration: 'none' }}>{t('legal.back')}</a>
        {md === undefined ? <p style={{ marginTop: 24 }}>{t('legal.loading')}</p>
          : md === null ? (
            <>
              <h1 style={{ fontSize: 26, margin: '24px 0 12px', color: '#111' }}>{doc === 'villkor' ? t('legal.terms') : t('legal.privacy')}</h1>
              <p>{t('legal.comingSoon')}</p>
            </>
          ) : renderMarkdown(md)}
      </div>
    </div>
  );
}

const link = (doc: LegalDoc) => (
  <a href={LEGAL_PATHS[doc]} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
    {doc === 'villkor' ? t('legal.terms').toLowerCase() : t('legal.privacy').toLowerCase()}
  </a>
);

export function TermsCheckbox({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: '#4a4a4a', lineHeight: 1.5, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ marginTop: 2 }} />
      <span>{tn('legal.acceptLabel', { terms: link('villkor'), privacy: link('integritetspolicy') })}</span>
    </label>
  );
}

/** Visas för inloggade som inte godkänt gällande villkorsversion. */
export function TermsUpdateModal({ version, onAccepted }: { version: string; onAccepted: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 28, maxWidth: 440, width: '100%' }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#111', marginBottom: 12 }}>{t('legal.updatedTitle')}</div>
        <div style={{ fontSize: 14, color: '#4a4a4a', lineHeight: 1.6 }}>{tn('legal.updatedBody', { terms: link('villkor'), privacy: link('integritetspolicy') })}</div>
        {error && <div style={{ fontSize: 13, color: '#c62828', marginTop: 10 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button disabled={busy} onClick={async () => {
            setBusy(true); setError('');
            try { await api.legalAccept(version); onAccepted(); }
            catch (e: any) { setError(e.message); setBusy(false); }
          }} style={{ padding: '10px 20px', border: 'none', borderRadius: 2, background: PRIMARY, color: '#fff', fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', cursor: 'pointer' }}>
            {busy ? '...' : t('legal.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
